import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { notificationAPI } from '@/lib/api';
import { Heartbeat, House, FileText, Bell, Gear, SignOut, List, ChartBar, Shield, ChartLineUp, Lightning } from '@phosphor-icons/react';

export default function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (user) {
      notificationAPI.unreadCount().then(r => setUnreadCount(r.data.unread_count)).catch(() => {});
      const interval = setInterval(() => {
        notificationAPI.unreadCount().then(r => setUnreadCount(r.data.unread_count)).catch(() => {});
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  if (!user) return null;

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: House },
    { path: '/reports', label: 'Reports', icon: FileText },
    { path: '/trends', label: 'Trends', icon: ChartLineUp },
    { path: '/alerts', label: 'Alerts', icon: Lightning },
    { path: '/compare', label: 'Compare', icon: ChartBar },
    { path: '/notifications', label: 'Notifications', icon: Bell, badge: unreadCount },
  ];

  if (user.role === 'admin' || user.role === 'super_admin' || user.role === 'moderator' || user.role === 'notification_admin' || user.role === 'security_admin') {
    navItems.push({ path: '/admin', label: 'Admin', icon: Shield });
  }

  return (
    <nav className="sticky top-0 z-50 bg-[#FAF9F6] border-b border-[#E5E3DB]" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/dashboard" className="flex items-center gap-2 group" data-testid="nav-logo">
            <Heartbeat size={28} weight="duotone" className="text-[#2C4A3B] group-hover:text-[#3C634F] transition-colors" />
            <span className="font-['Outfit'] font-semibold text-lg text-[#1E2922] tracking-tight">MedReport AI</span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-1">
            {navItems.map(item => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
                  ${isActive(item.path)
                    ? 'bg-[#2C4A3B] text-white'
                    : 'text-[#5C6B62] hover:bg-[#F2EFE9] hover:text-[#1E2922]'
                  }`}
              >
                <item.icon size={18} weight={isActive(item.path) ? 'fill' : 'regular'} />
                {item.label}
                {item.badge > 0 && (
                  <span className="ml-1 bg-[#E08A70] text-white text-xs rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                    {item.badge}
                  </span>
                )}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-2">
            <Link
              to="/settings"
              data-testid="nav-settings"
              className="p-2 rounded-lg text-[#5C6B62] hover:bg-[#F2EFE9] hover:text-[#1E2922] transition-all"
            >
              <Gear size={20} />
            </Link>
            <button
              onClick={handleLogout}
              data-testid="nav-logout"
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-[#D96C6C] hover:bg-red-50 transition-all"
            >
              <SignOut size={18} />
              Logout
            </button>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 rounded-lg text-[#5C6B62] hover:bg-[#F2EFE9]"
            data-testid="mobile-menu-toggle"
          >
            <List size={24} />
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden pb-4 space-y-1">
            {navItems.map(item => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all
                  ${isActive(item.path) ? 'bg-[#2C4A3B] text-white' : 'text-[#5C6B62] hover:bg-[#F2EFE9]'}`}
              >
                <item.icon size={20} weight={isActive(item.path) ? 'fill' : 'regular'} />
                {item.label}
                {item.badge > 0 && (
                  <span className="ml-auto bg-[#E08A70] text-white text-xs rounded-full px-1.5 py-0.5">{item.badge}</span>
                )}
              </Link>
            ))}
            <Link
              to="/settings"
              onClick={() => setMobileOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all
                ${isActive('/settings') ? 'bg-[#2C4A3B] text-white' : 'text-[#5C6B62] hover:bg-[#F2EFE9]'}`}
            >
              <Gear size={20} weight={isActive('/settings') ? 'fill' : 'regular'} />
              Settings
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium text-[#D96C6C] hover:bg-red-50"
            >
              <SignOut size={20} />
              Logout
            </button>
          </div>
        )}
      </div>
    </nav>
  );
}
